/// <amd-module name="Wit/Common/Predicate" />

import { FieldType } from "Wit/Clients/Work/Contracts/Work";
import { WiqlOperators } from "./WiqlOperators";

// interface for fetching workitem field values and types required for evaluating the wiql expression tree
// Copied mainly from Tfs/Service/WebAccess/Agile/Scripts/Common/Predicate.ts
export interface IPredicateEvaluationHelper {
    // Retrieve the field value corresponding to fieldReferenceName, and compare it with 'value'
    // < 0 : lesser
    // = 0 : equal
    // > 0 : greater
    // = Number.NEGATIVE_INFINITY - means nothing to compare(example: empty date)
    compare: (fieldReferenceName: string, value: any) => Promise<number>;

    contains: (fieldReferenceName: string, value: any) => boolean;

    under: (fieldReferenceName: string, value: any) => boolean;
}

// interface for getting the list of supported fields, operators and macros.
export interface IPredicateConfiguration {
    getSupportedFieldTypes: () => FieldType[];

    getSupportedOperators: (fieldType: FieldType) => string[];

    getSupportedMacros: (fieldType: FieldType) => string[];
}

export interface IPredicateVisitor {
    (currentPredicate: PredicateOperator, parentPredicate: PredicateOperator): boolean;
}

export class PredicateOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return false;
    }

    public walk(parent: PredicateOperator, before: IPredicateVisitor, after: IPredicateVisitor): boolean {
        if (before && before(this, parent)) {
            if (after) {
                return after(this, parent);
            } else {
                return true;
            }
        } else {
            return false;
        }
    }
}

export class PredicateUnaryOperator extends PredicateOperator {
    public operand: PredicateOperator;

    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return this.operand.evaluate(helper);
    }

    public walk(parent: PredicateOperator, before: IPredicateVisitor, after: IPredicateVisitor): boolean {
        return super.walk(parent, before, () => {
            if (this.operand) {
                if (!this.operand.walk(this, before, after)) {
                    return false;
                }
            }

            if (after) {
                return after(this, parent);
            } else {
                return true;
            }
        });
    }
}

export enum OperatorPrecendence {
    Low,
    High
}

export class PredicateOpenParenthesesOperator extends PredicateOperator {
    constructor() {
        super();
    }
}

export class PredicateCloseParenthesesOperator extends PredicateOperator {
    constructor() {
        super();
    }
}

export class PredicateBinaryOperator extends PredicateOperator {
    public operands: PredicateOperator[];
    public precedence: OperatorPrecendence;

    public walk(parent: PredicateOperator, before: IPredicateVisitor, after: IPredicateVisitor): boolean {
        return super.walk(parent, before, () => {
            if (this.operands) {
                var length: number = this.operands.length;
                var i: number;
                for (i = 0; i < length; i++) {
                    if (!this.operands[i].walk(this, before, after)) {
                        return false;
                    }
                }
            }

            if (after) {
                return after(this, parent);
            } else {
                return true;
            }
        });
    }
}

export class PredicateAndOperator extends PredicateBinaryOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        if (this.operands) {
            var length: number = this.operands.length;
            var i: number;
            for (i = 0; i < length; i++) {
                if (!this.operands[i].evaluate(helper)) {
                    return false;
                }
            }
        }
        return true;
    }

    public precedence: OperatorPrecendence = OperatorPrecendence.High;
}

export class PredicateOrOperator extends PredicateBinaryOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        if (this.operands) {
            var length: number = this.operands.length;
            var i: number;
            for (i = 0; i < length; i++) {
                if (await this.operands[i].evaluate(helper)) {
                    return true;
                }
            }
        }
        return false;
    }

    public precedence: OperatorPrecendence = OperatorPrecendence.Low;
}

export class PredicateFieldOperator extends PredicateOperator {
    public field: string;
    public value: any;
    constructor(field: string, value: any) {
        super();
        this.field = field;
        this.value = value;
    }
}

export class PredicateFieldComparisonOperator extends PredicateFieldOperator {
    public async compare(helper: IPredicateEvaluationHelper): Promise<number> {
        return helper.compare(this.field, this.value);
    }
}

export class PredicateEqualsOperator extends PredicateFieldComparisonOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return (await this.compare(helper)) === 0;
    }
}

export class PredicateNotEqualsOperator extends PredicateFieldComparisonOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return (await this.compare(helper)) !== 0;
    }
}

export class PredicateGreaterThanOperator extends PredicateFieldComparisonOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        const result = await this.compare(helper);
        return result > 0 && result != Number.NEGATIVE_INFINITY;
    }
}

export class PredicateGreaterThanOrEqualToOperator extends PredicateFieldComparisonOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        const result = await this.compare(helper);
        return result >= 0 && result != Number.NEGATIVE_INFINITY;
    }
}

export class PredicateLessThanOperator extends PredicateFieldComparisonOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        const result = await this.compare(helper);
        return result < 0 && result != Number.NEGATIVE_INFINITY;
    }
}

export class PredicateLessThanOrEqualToOperator extends PredicateFieldComparisonOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        const result = await this.compare(helper);
        return result <= 0 && result != Number.NEGATIVE_INFINITY;
    }
}

export class PredicateFieldContainmentOperator extends PredicateFieldOperator {
    public contains(helper: IPredicateEvaluationHelper): boolean {
        return helper.contains(this.field, this.value);
    }
}

export class PredicateContainsOperator extends PredicateFieldContainmentOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return this.contains(helper) === true;
    }
}

export class PredicateNotContainsOperator extends PredicateFieldContainmentOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return this.contains(helper) === false;
    }
}

export class PredicateFieldTreePathOperator extends PredicateFieldOperator {
    public under(helper: IPredicateEvaluationHelper): boolean {
        return helper.under(this.field, this.value);
    }
}

export class PredicateUnderOperator extends PredicateFieldTreePathOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return this.under(helper) === true;
    }
}

export class PredicateNotUnderOperator extends PredicateFieldTreePathOperator {
    public async evaluate(helper: IPredicateEvaluationHelper): Promise<boolean> {
        return this.under(helper) === false;
    }
}

export function getLogicalOperator(inVariantOperator: string): PredicateBinaryOperator {
    switch (inVariantOperator.toLocaleUpperCase()) {
        case WiqlOperators.OperatorAnd:
            return new PredicateAndOperator();
        case WiqlOperators.OperatorOr:
            return new PredicateOrOperator();
    }

    return new PredicateAndOperator();
}

export function getFieldOperator(inVariantOperator: string, field: string, value: any): PredicateOperator {
    switch (inVariantOperator.toLocaleUpperCase()) {
        case WiqlOperators.OperatorEqualTo:
            return new PredicateEqualsOperator(field, value);
        case WiqlOperators.OperatorNotEqualTo:
            return new PredicateNotEqualsOperator(field, value);
        case WiqlOperators.OperatorLessThan:
            return new PredicateLessThanOperator(field, value);
        case WiqlOperators.OperatorLessThanOrEqualTo:
            return new PredicateLessThanOrEqualToOperator(field, value);
        case WiqlOperators.OperatorGreaterThan:
            return new PredicateGreaterThanOperator(field, value);
        case WiqlOperators.OperatorGreaterThanOrEqualTo:
            return new PredicateGreaterThanOrEqualToOperator(field, value);
        case WiqlOperators.OperatorContains:
            return new PredicateContainsOperator(field, value);
        case WiqlOperators.OperatorNotContains:
            return new PredicateNotContainsOperator(field, value);
        case WiqlOperators.OperatorUnder:
            return new PredicateUnderOperator(field, value);
        case WiqlOperators.OperatorNotUnder:
            return new PredicateNotUnderOperator(field, value);
    }

    return new PredicateEqualsOperator(field, value);
}
